import { fetch_company_cases } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_live_queue';
import {
  first_present,
  is_open_case_status,
  normalize_key,
  normalize_text,
  read_session_current_user_id,
  to_number,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import { case_status_key, status_dot_class, status_dot_style } from './case_state';

function workflow_status_option(status = {}, index = 0) {
  const label = normalize_text(
    status?.status_name
    || status?.workflow_status_name
    || status?.status
    || status?.name
    || `Status ${index + 1}`
  );
  const id = to_number(
    status?.id
    || status?.assigned_form_status_id
    || status?.status_id
    || status?.workflow_status_id
  );
  const order = Number(first_present([
    status?.order_number,
    status?.order,
    status?.priority,
    index,
  ]) || index);

  return label
      ? {
          id: id || `${normalize_key(label)}-${order}`,
          key: case_status_key(label),
          label,
          color: status?.status_color || status?.color_code || status?.color || null,
          order,
      is_final: Boolean(status?.is_final),
    }
    : null;
}

function unique_by(items = [], key_builder = (item) => item) {
  const seen = new Set();
  return items.filter((item) => {
    const key = key_builder(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function sort_statuses(statuses = []) {
  return statuses.slice().sort((left, right) => {
    const order_diff = Number(left?.order || 0) - Number(right?.order || 0);
    if (order_diff) return order_diff;
    return String(left?.label || '').localeCompare(String(right?.label || ''));
  });
}

function status_dot_tone(status_label = '') {
  const dot_class = status_dot_class(status_label);
  if (dot_class === 'bg-warning') return 'is-warning';
  if (dot_class === 'bg-secondary-subtle') return 'is-muted';

  const normalized = case_status_key(status_label);
  if (['due', 'overdue', 'breach', 'breached', 'escalated'].some((token) => normalized.includes(token))) {
    return 'is-danger';
  }

  return 'is-success';
}

function count_by_status(cases = []) {
  return cases.reduce((counts, case_item) => {
    const key = case_status_key(case_item?.status);
    if (!key) return counts;
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
}

function normalized_sla_text(case_item = {}) {
  return normalize_text([
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ].filter(Boolean).join(' ')).toLowerCase();
}

function is_sla_warning_case(case_item = {}) {
  const value = normalized_sla_text(case_item);
  return value.includes('left')
    || value.includes('warning')
    || value.includes('at-risk')
    || value.includes('due');
}

function is_sla_breached_case(case_item = {}) {
  const value = normalized_sla_text(case_item);
  return value.includes('overdue')
    || value.includes('breach')
    || value.includes('breached');
}

async function assigned_cases_only(cases = []) {
  const current_user_id = read_session_current_user_id();
  if (!current_user_id) return cases;

  return cases.filter((case_item) =>
    (Array.isArray(case_item?.assignment_user_ids) ? case_item.assignment_user_ids : [])
      .some((user_id) => Number(user_id) === Number(current_user_id))
  );
}

async function workflow_statuses_for_cases(cases = []) {
  return sort_statuses(unique_by(
    cases
      .map((case_item, index) => workflow_status_option({
        status_name: case_item?.status,
        status_color: case_item?.status_color,
        order: index
      }, index))
      .filter(Boolean),
    (status) => status.key
  ));
}

function fallback_statuses(cases = []) {
  return sort_statuses(unique_by(
    cases
      .map((case_item, index) => workflow_status_option({
        status_name: case_item?.status,
        status_color: case_item?.status_color,
        order: index
      }, index))
      .filter(Boolean),
    (status) => status.key
  ));
}

export async function fetch_case_owner_cases(project_id = null) {
  const cases = await fetch_company_cases(project_id);
  return assigned_cases_only(cases);
}

export async function fetch_case_owner_status_items(project_id = null) {
  const cases = await fetch_case_owner_cases(project_id);
  const configured_statuses = await workflow_statuses_for_cases(cases);
  const statuses = configured_statuses.length ? configured_statuses : fallback_statuses(cases);
  const counts = count_by_status(cases);

  return statuses.map((status) => ({
    id: `status-${status.key}`,
    label: status.label,
    route_name: 'OwnerQueue',
    route_query: { status: status.key },
    count: String(counts[status.key] || 0),
    dot_class: status_dot_tone(status.label),
    status_color: status.color,
    dot_style: status_dot_style(status.color),
  }));
}

export function filter_cases_by_status(cases = [], status_key = '') {
  const normalized_status_key = case_status_key(status_key);
  if (!normalized_status_key) return cases;
  return cases.filter((case_item) => case_status_key(case_item?.status) === normalized_status_key);
}

export function case_queue_metrics(cases = []) {
  const open_cases = cases.filter((case_item) => is_open_case_status(case_item?.status));
  const warning_cases = cases.filter((case_item) => is_sla_warning_case(case_item) && !is_sla_breached_case(case_item));
  const breached_cases = cases.filter(is_sla_breached_case);

  return [
    { label: 'Open Cases', value: String(open_cases.length), change: 'Live queue', change_class: 'secondary-color', icon: 'bi-folder2-open' },
    { label: 'SLA Warning', value: String(warning_cases.length), change: '<= 48hr to breach', change_class: 'text-secondary', icon: 'bi-clock-history' },
    { label: 'SLA Breached', value: String(breached_cases.length), change: breached_cases.length ? 'Action required' : 'None', change_class: 'text-secondary', icon: 'bi-exclamation-triangle' },
    { label: 'Avg. Resolution', value: '-', change: 'Cases', change_class: 'secondary-color', icon: 'bi-arrow-down-left' }
  ];
}
